import { LiveAnnouncer } from '@angular/cdk/a11y';
import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import type {
  ProgressBarCircularSize,
  ProgressBarCompleteEvent,
  ProgressBarContext,
  ProgressBarMode,
  ProgressBarSegment,
  ProgressBarSize,
  ProgressBarValueChangeEvent,
  ProgressBarVariant,
} from './progress-bar.types';
import { CIRCULAR_SIZE_MAP, CIRCULAR_STROKE_MAP, clampProgressValue, isCircularSize } from './progress-bar.types';

/**
 * Progress indicator component supporting both linear and circular variants, determinate and indeterminate modes,
 * multi-segment progress, and rich accessibility.
 *
 * ## Features
 *
 * - Linear and circular variants
 * - Determinate (0-100%) and indeterminate (loading) modes
 * - 6 semantic color contexts (brand, neutral, positive, negative, caution, info)
 * - Multi-segment progress for workflows
 * - Pin/knob indicators for milestones
 * - Full WCAG 2.1 AA accessibility with screen reader support
 * - Reduced motion and RTL support
 *
 * @example
 *   Basic Linear Progress
 *   ```html
 *   <hx-progress-bar [value]="50" />
 *   ```
 *
 * @example
 *   Circular Loading Spinner
 *   ```html
 *   <hx-progress-bar variant="circular" mode="indeterminate" />
 *   ```
 *
 * @example
 *   File Upload with Status
 *   ```html
 *   <hx-progress-bar
 *   [value]="uploadProgress"
 *   title="Uploading files..."
 *   status="3 of 10 complete"
 *   context="positive" />
 *   ```
 *
 * @example
 *   Multi-Segment Workflow
 *   ```typescript
 *   segments = [
 *   { value: 30, label: 'Setup', context: 'positive' },
 *   { value: 20, label: 'Processing', context: 'brand' }
 *   ];
 *   ```
 *   ```html
 *   <hx-progress-bar [segments]="segments" />
 *   ```
 *
 * @example
 *   With Pin Indicator
 *   ```html
 *   <hx-progress-bar
 *   [value]="75"
 *   [showPin]="true"
 *   pinLabel="Step 3 of 4" />
 *   ```
 *
 * @example
 *   Listening to Events
 *   ```html
 *   <hx-progress-bar
 *   [value]="progress"
 *   (valueChange)="onProgressChange($event)"
 *   (complete)="onUploadComplete()" />
 *   ```
 */
@Component({
  selector: 'hx-progress-bar',
  standalone: true,
  templateUrl: './progress-bar.html',
  styleUrl: './progress-bar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'hostClasses()',
    '[attr.role]': '"progressbar"',
    '[attr.aria-valuemin]': '0',
    '[attr.aria-valuemax]': '100',
    '[attr.aria-valuenow]': 'ariaValueNow()',
    '[attr.aria-label]': 'computedAriaLabel()',
    '[attr.aria-valuetext]': 'ariaValueText()',
    '[attr.aria-busy]': 'isIndeterminate() || null',
    '[attr.tabindex]': '-1',
  },
})
export class HxProgressBar extends HxBaseControl {
  // ========================================================================
  // Dependencies
  // ========================================================================

  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly platformId = inject(PLATFORM_ID);

  // ========================================================================
  // Component Inputs (Signal-based)
  // ========================================================================

  /** Current progress value (0-100). Only applies in determinate mode. */
  readonly value = input<number>(0);

  /** Progress mode: determinate (known value) or indeterminate (loading). */
  readonly mode = input<ProgressBarMode>('determinate');

  /** Visual variant: linear bar or circular spinner. */
  readonly variant = input<ProgressBarVariant>('linear');

  /**
   * Size of the progress indicator. Linear: 6xs/5xs/4xs/3xs/2xs (2/4/8/12/16px) Circular: 2xs/xs/sm/md/lg
   * (16/24/32/40/48px)
   */
  readonly size = input<ProgressBarSize>('2xs');

  /** Semantic color context. */
  readonly context = input<ProgressBarContext>('brand');

  /** Array of segments for multi-segment progress. Only applies to linear variant. */
  readonly segments = input<ProgressBarSegment[]>();

  /** Show pin/knob indicator above the progress bar. Only applies to linear variant in determinate mode. */
  readonly showPin = input<boolean>(false);

  /** Custom label for the pin tooltip. */
  readonly pinLabel = input<string>();

  /** Title text displayed above the progress bar. */
  readonly title = input<string>();

  /** Status text displayed below the progress bar. */
  readonly status = input<string>();

  /** ARIA label for the progress bar. */
  override readonly ariaLabel = input<string>();

  /** ARIA value text for custom progress descriptions. */
  readonly ariaValueText = input<string>();

  /** Disable screen reader announcements for value changes. */
  readonly disableAnnouncements = input<boolean>(false);

  /** Additional CSS classes to apply to the host element. */
  readonly className = input<string>();

  /** Disable animations (also respects prefers-reduced-motion). */
  readonly disableAnimations = input<boolean>(false);

  // ========================================================================
  // Component Outputs
  // ========================================================================

  /** Emits when the progress value changes. */
  valueChange = output<ProgressBarValueChangeEvent>();

  /** Emits when progress reaches 100%. */
  complete = output<ProgressBarCompleteEvent>();

  // ========================================================================
  // Internal State
  // ========================================================================

  private readonly previousValue = signal<number>(0);
  private readonly lastAnnouncedValue = signal<number>(0);

  // ========================================================================
  // Computed Properties
  // ========================================================================

  /** Clamped progress value (0-100). */
  readonly clampedValue = computed(() => clampProgressValue(this.value()));

  /** Whether the component is in indeterminate mode. */
  readonly isIndeterminate = computed(() => this.mode() === 'indeterminate');

  /** Whether to show the pin indicator. */
  readonly shouldShowPin = computed(() => this.showPin() && this.variant() === 'linear' && !this.isIndeterminate());

  /** CSS classes for the host element. */
  readonly hostClasses = computed(() => {
    const classes = [
      'hx-progress-bar',
      `hx-progress-bar--${this.variant()}`,
      `hx-progress-bar--${this.mode()}`,
      `hx-progress-bar--context-${this.context()}`,
      `hx-progress-bar--size-${this.size()}`,
    ];

    if (this.className()) {
      classes.push(this.className()!);
    }

    return classes.join(' ');
  });

  /** Current ARIA label (computed from input or default). */
  readonly computedAriaLabel = computed(() => {
    if (this.ariaLabel()) {
      return this.ariaLabel()!;
    }

    const modeStr = this.isIndeterminate() ? 'loading' : 'determinate';
    const variantStr = this.variant() === 'circular' ? 'spinner' : 'progress bar';
    return `${modeStr} ${variantStr}`;
  });

  /** Current ARIA value (null in indeterminate mode). */
  readonly ariaValueNow = computed(() => (this.isIndeterminate() ? null : this.clampedValue()));

  /** Whether the user prefers reduced motion. */
  readonly prefersReducedMotion = computed(() => {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  /** Whether to show animations. */
  readonly shouldAnimate = computed(() => !this.disableAnimations() && !this.prefersReducedMotion());

  // ========================================================================
  // Circular Variant Computed Properties
  // ========================================================================

  /** Current diameter based on size. */
  readonly diameter = computed(() => {
    const size = this.size();
    if (isCircularSize(size)) {
      return CIRCULAR_SIZE_MAP[size as ProgressBarCircularSize];
    }
    return CIRCULAR_SIZE_MAP['sm']; // Default
  });

  /** Current stroke width based on size. */
  readonly strokeWidth = computed(() => {
    const size = this.size();
    if (isCircularSize(size)) {
      return CIRCULAR_STROKE_MAP[size as ProgressBarCircularSize];
    }
    return CIRCULAR_STROKE_MAP['sm']; // Default
  });

  /** Circle radius calculated from diameter and stroke width. */
  readonly radius = computed(() => (this.diameter() - this.strokeWidth()) / 2);

  /** Circle circumference. */
  readonly circumference = computed(() => 2 * Math.PI * this.radius());

  /** SVG viewBox attribute value. */
  readonly viewBox = computed(() => {
    const d = this.diameter();
    return `0 0 ${d} ${d}`;
  });

  /** Stroke dash offset for determinate progress arc. */
  readonly strokeDashOffset = computed(() => this.circumference() * (1 - this.clampedValue() / 100));

  // ========================================================================
  // Linear Variant Computed Properties
  // ========================================================================

  /** CSS transform value for the progress fill. */
  readonly fillTransform = computed(() => `scaleX(${this.clampedValue() / 100})`);

  /** Total value of all segments. */
  readonly totalSegmentValue = computed(() => {
    const segs = this.segments();
    if (!segs) {
      return 0;
    }
    return segs.reduce((sum, seg) => sum + seg.value, 0);
  });

  /** Pin position as a percentage. */
  readonly pinPosition = computed(() => this.clampedValue());

  // ========================================================================
  // Effects
  // ========================================================================

  constructor() {
    super();
    // Track value changes and emit events with throttling
    let lastUpdateTime = 0;
    const throttleMs = 16; // 60fps = ~16ms per frame

    effect(() => {
      const currentValue = this.clampedValue();
      const prevValue = this.previousValue();
      const rawValue = this.value();

      // Development mode warning for invalid values
      if (typeof ngDevMode !== 'undefined' && ngDevMode) {
        if (rawValue < 0 || rawValue > 100) {
          console.warn(
            `[HxProgressBar] Invalid value: ${rawValue}. Value must be between 0 and 100. ` +
              `The value has been clamped to ${currentValue}.`
          );
        }
      }

      if (currentValue !== prevValue) {
        const now = Date.now();
        const timeSinceLastUpdate = now - lastUpdateTime;

        // Throttle updates to 60fps (except for 0 and 100)
        if (currentValue === 0 || currentValue === 100 || timeSinceLastUpdate >= throttleMs) {
          lastUpdateTime = now;

          const crossedThreshold = this.shouldAnnounceProgress(currentValue);

          // Emit value change event
          this.valueChange.emit({
            value: currentValue,
            previousValue: prevValue,
            crossedThreshold,
          });

          // Announce to screen readers if threshold crossed
          if (crossedThreshold && !this.disableAnnouncements()) {
            this.announceProgress();
          }

          // Check for completion
          if (currentValue === 100 && prevValue < 100) {
            this.complete.emit({
              value: 100,
              timestamp: Date.now(),
            });
          }
        }

        // Always update previous value for next comparison
        this.previousValue.set(currentValue);
      }
    });
  }

  // ========================================================================
  // Public Methods
  // ========================================================================

  /** Checks if the current value change should trigger an announcement. */
  shouldAnnounceProgress(newValue: number): boolean {
    const threshold = 10; // Announce every 10%
    const lastAnnounced = this.lastAnnouncedValue();

    // Calculate which threshold bracket each value falls into
    const newBracket = Math.floor(newValue / threshold);
    const lastBracket = Math.floor(lastAnnounced / threshold);

    return newBracket !== lastBracket;
  }

  /** Manually trigger a progress announcement. */
  announceProgress(message?: string): void {
    const msg = message || `${this.clampedValue()}% complete`;
    this.liveAnnouncer.announce(msg, 'polite');
    this.lastAnnouncedValue.set(this.clampedValue());
  }

  /** Resets the progress bar to initial state. */
  reset(): void {
    this.previousValue.set(0);
    this.lastAnnouncedValue.set(0);
  }

  /** Sets progress to complete. */
  setComplete(): void {
    this.previousValue.set(100);
  }
}
